import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from './auth.service';

/**
 * Sends the user back through sign-in when the API stops accepting their session.
 *
 * Called by `apiErrorInterceptor` on a 401. It signs out of this app, which clears MSAL's cached
 * account and tokens and replaces the page with the login screen, and records where the user was
 * so the login page can return them there. Clearing the cache is the point: the API rejected the
 * token MSAL holds, so a silent token refresh would hand the same session straight back.
 *
 * Loop guard: once a re-authentication starts, further 401s in this tab are left to surface as
 * ordinary errors for `COOLDOWN_MS`. That covers the burst of 401s from parallel requests on one
 * page, and the case a fresh sign-in does not fix (the account row deleted, say), where sending
 * the user back again would only repeat the round trip. The timestamp lives in sessionStorage
 * because signing out reloads the page and takes any in-memory state with it; an in-memory copy
 * backs it up for a browser that refuses storage.
 *
 * The cooldown is not cleared by signing in again, deliberately: a 401 straight after a fresh
 * sign-in is exactly the case it exists for. The cost is that a second, unrelated expiry within
 * the window shows errors in place instead of signing out.
 */
@Injectable({
  providedIn: 'root',
})
export class ReauthenticationService {
  private authService = inject(AuthService);
  private router = inject(Router);

  static readonly STORAGE_KEY = 'reauthenticationRequestedAt';
  static readonly COOLDOWN_MS = 5 * 60 * 1000;

  private lastRequestedAt = 0;

  /**
   * Starts a re-authentication unless one started within the cooldown, or the user is already on
   * the login page (signing out would only reload it). Returns whether it started one.
   */
  reauthenticate(): boolean {
    const targetUrl = this.targetUrl();
    if (isLoginPage(targetUrl)) {
      return false;
    }

    const now = Date.now();
    if (this.isCoolingDown(now)) {
      return false;
    }

    this.lastRequestedAt = now;
    writeSession(ReauthenticationService.STORAGE_KEY, String(now));
    // The login page reads this key after sign-in, the same one authGuard writes.
    writeSession('redirectUrl', targetUrl);

    // logout() still replaces the page when the cache clear fails; the rejection only needs logging.
    this.authService.logout().catch((error) => {
      console.error('Signing out before re-authentication failed:', error);
    });
    return true;
  }

  /**
   * Where sign-in should return the user. While a navigation is still running — a deep link whose
   * route guard got the 401 from `/api/auth/me` — `router.url` is still the page before it (`/` on
   * a fresh load), so the navigation's target is the page the user asked for.
   */
  private targetUrl(): string {
    const navigation = this.router.currentNavigation();
    const target = navigation?.finalUrl ?? navigation?.extractedUrl;
    return target ? this.router.serializeUrl(target) : (this.router.url ?? '');
  }

  private isCoolingDown(now: number): boolean {
    const stored = Number(readSession(ReauthenticationService.STORAGE_KEY));
    const last = Math.max(
      Number.isFinite(stored) ? stored : 0,
      this.lastRequestedAt,
    );
    // Math.abs so a clock set backwards cannot hold the guard shut for longer than the cooldown.
    return (
      last > 0 && Math.abs(now - last) < ReauthenticationService.COOLDOWN_MS
    );
  }
}

/** `''` and `/login` both route to the login page, where signing out would only reload it. */
function isLoginPage(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return path === '' || path === '/' || path.startsWith('/login');
}

/** sessionStorage can throw (storage disabled, quota); this runs inside an error handler. */
function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // The in-memory copy still guards this page.
  }
}
